import { useEffect, useRef } from "react";

import { gql, useLazyQuery, useQuery } from "@apollo/client";

import {
  ATTACHMENT_FRAGMENT,
  AttachmentFragment,
  CLIENT_FRAGMENT,
  ClientFragment,
  FREELANCE_PROFILE_FRAGMENT,
  FreelanceProfileFragment,
  REFERENCE_PERSON_FRAGMENT,
  ReferencePersonFragment,
} from "graphql/fragments";
import { Invoice, QueryInvoicesArgs } from "graphql/types";

import { PaginatedPayload } from "./shared/types";

const SIMPLIFIED_INVOICES = gql`
  query (
    $page: Int
    $perPage: Int
    $filter: InvoicesFilter
    $sortBy: InvoicesSortByEnum
  ) {
    invoices(page: $page, perPage: $perPage, filter: $filter, sortBy: $sortBy) {
      exportPath
      items {
        id
        client {
          ...ClientFragment
        }
        clientReferencePerson {
          ...ReferencePersonFragment
        }
        freelanceProfile {
          ...FreelanceProfileFragment
        }
        submittedAt
        rejectedAt
        publishedAt
        resolvedAt
        dueOn
        invoiceNumber
        status
        amount
        vat
        payoutAmount
        refundStatus
        pdfDocument {
          ...AttachmentFragment
        }
      }
      total
    }
  }
  ${ATTACHMENT_FRAGMENT}
  ${CLIENT_FRAGMENT}
  ${FREELANCE_PROFILE_FRAGMENT}
  ${REFERENCE_PERSON_FRAGMENT}
`;

export interface SimpleInvoice
  extends Pick<
    Invoice,
    | "id"
    | "submittedAt"
    | "rejectedAt"
    | "publishedAt"
    | "resolvedAt"
    | "dueOn"
    | "invoiceNumber"
    | "status"
    | "amount"
    | "vat"
    | "payoutAmount"
    | "refundStatus"
    | "currency"
  > {
  client?: ClientFragment;
  clientReferencePerson?: ReferencePersonFragment;
  freelanceProfile: FreelanceProfileFragment;
  pdfDocument?: AttachmentFragment;
}

interface SimplifiedInvoicesQuery {
  invoices: PaginatedPayload<SimpleInvoice>;
}

type QueryArgs = Pick<
  QueryInvoicesArgs,
  "page" | "perPage" | "filter" | "sortBy"
>;

export const useSimplifiedInvoicesQuery = (args: QueryArgs = {}) => {
  const { data, loading: isLoading } = useQuery<
    SimplifiedInvoicesQuery,
    QueryArgs
  >(SIMPLIFIED_INVOICES, {
    variables: { ...args },
  });

  const invoices = data?.invoices.items?.filter(Boolean) ?? [];

  return {
    invoices,
    isLoading,
  };
};

export const useSimplifiedInvoicesLazyQuery = (): {
  getInvoices: (args?: QueryArgs) => Promise<SimpleInvoice[] | undefined>;
  invoices: SimpleInvoice[];
  total: number;
  isLoading: boolean;
  called: boolean;
} => {
  const [getInvoicesQuery, { data, loading: isLoading, called }] = useLazyQuery<
    SimplifiedInvoicesQuery,
    QueryArgs
  >(SIMPLIFIED_INVOICES, {
    fetchPolicy: "network-only",
  });

  const invoices = data?.invoices.items?.filter(Boolean) ?? [];
  const total = data?.invoices.total ?? 0;

  const resolveRef = useRef<(invoices?: SimpleInvoice[]) => void>(undefined);

  useEffect(() => {
    if (called && !isLoading && resolveRef.current) {
      resolveRef.current(invoices);
      resolveRef.current = undefined;
    }
  }, [invoices, called, isLoading]);

  const getInvoices = async (args?: QueryArgs) => {
    const variables = { ...args };

    getInvoicesQuery({ variables });

    return new Promise<SimpleInvoice[] | undefined>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  return {
    getInvoices,
    invoices,
    total,
    isLoading,
    called,
  };
};
